import { combineReducers, configureStore, type Reducer } from '@reduxjs/toolkit';
import authReducer from './slices/authSlice';
import invoiceReducer from './slices/invoiceSlice';
import profileReducer from './slices/profileSlice';
import licenseReducer from './slices/licenseSlice';
import errorReducer from './slices/errorSlice';
import notificationReducer from './slices/notificationSlice';
import dataSourceReducer from './slices/dataSourceSlice';
import freshdeskReducer from './slices/freshdeskSlice';
import loadingReducer from './slices/loadingSlice';
import languageReducer from './slices/languageSlice';
import themeReducer from './slices/themeSlice';
import userReducer from './slices/userSlice';
import contactUsReducer from './slices/contactUsSlice';
import gpsrProductsReducer from "./slices/gpsrProductsSlice";
import gpsrQuestionsListReducer from "./slices/gpsrQuestionsListSlice";
import lieferbarReducer from "./slices/lieferbarSlice";
import overageReducer from "./slices/overageSlice";
import subusersReducer from "./slices/subusersSlice";
import twoFactorReducer from "./slices/twoFactorSlice";
import contentMasterReducer from "./slices/contentMasterSlice";
import marketplaceListingReducer from "./slices/marketplaceListingSlice";
import taskReducer from "./slices/taskSlice";
import notificationFeedReducer from "./slices/notificationFeedSlice";
import googleReviewReducer from "./slices/googleReviewSlice";
import googleReviewSettingsReducer from "./slices/googleReviewSettingsSlice";
import shipmentTrackerReducer from "./slices/shipmentTrackerSlice";
import affiliateReducer from "./slices/affiliateSlice";

const appReducer = combineReducers({
  auth: authReducer,
  invoices: invoiceReducer,
  profile: profileReducer,
  licenses: licenseReducer,
  error: errorReducer,
  notifications: notificationReducer,
  dataSource: dataSourceReducer,
  freshdesk: freshdeskReducer,
  loading: loadingReducer,
  language: languageReducer,
  theme: themeReducer,
  user: userReducer,
  contactUs: contactUsReducer,
  gpsrProducts: gpsrProductsReducer,
  gpsrQuestionsList: gpsrQuestionsListReducer,
  lieferbar: lieferbarReducer,
  overage: overageReducer,
  subusers: subusersReducer,
  twoFactor: twoFactorReducer,
  contentMaster: contentMasterReducer,
  marketplaceListing: marketplaceListingReducer,
  tasks: taskReducer,
  notificationFeed: notificationFeedReducer,
  googleReview: googleReviewReducer,
  googleReviewSettings: googleReviewSettingsReducer,
  shipmentTracker: shipmentTrackerReducer,
  affiliate: affiliateReducer,
});

type AppState = ReturnType<typeof appReducer>;

/**
 * Slices that hold UI / device-level state and MUST survive a logout
 * (theme, language, etc.). Everything else is wiped on `auth/logout` so the
 * next user logs in to a clean store - no leftover subscriptions, no stale
 * `contentMaster.filters.source` from the previous account, no cached
 * dataSource. Without this, the next login starts with the previous user's
 * data and re-triggers the very races we just fixed.
 */
const PERSIST_ON_LOGOUT_KEYS: ReadonlyArray<keyof AppState> = [
  'language',
  'theme',
];

const rootReducer: Reducer<AppState> = (state, action) => {
  if (action.type === 'auth/logout' && state) {
    const preserved: Partial<AppState> = {};
    for (const key of PERSIST_ON_LOGOUT_KEYS) {
      preserved[key] = state[key] as AppState[typeof key] as never;
    }
    // Run the logout reducer through the regular pipeline so each slice's
    // own `logout` extraReducer (e.g. `auth/logout` clearing the auth state
    // + bootstrap guard) still fires on the preserved+empty state.
    return appReducer(preserved as AppState, action);
  }
  return appReducer(state, action);
};

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export type AppStoreDispatch = AppDispatch;
